<ng-container *transloco="let t">
  <div class="flex h-screen w-full flex-col bg-background-primary">
    <a class="relative mt-4 h-8 cursor-pointer" routerLink="/home">
      <div class="absolute left-4 top-0 flex items-center gap-1" [animateFade]="!collapsed">
        <img class="h-8" src="https://cdn.pixacare.com/logo/left-transparent.svg" />
        @if (environmentName && !collapsed) {
          <p [ngClass]="['text-sm', environmentColor]">{{ environmentName | lowercase }}</p>
        }
      </div>

      <img
        class="absolute left-4 top-0 h-8 w-8"
        src="https://cdn.pixacare.com/logo/mire.svg"
        [animateFade]="collapsed" />
    </a>

    @if (sidebarService.clients$ | async; as clients) {
      <tui-scrollbar class="max-h-full w-full flex-1 overscroll-contain" tuiFade="vertical">
        <div class="flex flex-col divide-y divide-border-tertiary">
          @if (menuBuilderService.clientMenuEntries$ | async; as clientEntries) {
            <ng-template #clientPicker>
              <pxc-client-picker
                class="block"
                data-testid="select-client"
                size="small"
                tuiHintDirection="end"
                [clients]="clients"
                [collapsed]="collapsed"
                [selectedClient]="sidebarService.activeClientCode$ | async"
                [tuiHint]="collapsed ? t('Sélectionnez un espace') : null"
                (selectedClientChange)="changeActiveClient($event)" />
            </ng-template>
            <ng-container
              [ngTemplateOutlet]="navigationSection"
              [ngTemplateOutletContext]="{
                entries: clientEntries,
                header: clientPicker,
              }" />
          }
          @if (sidebarService.areDepartmentsLoaded$ | async) {
            @if (menuBuilderService.departmentsEntries$ | async; as departmentEntries) {
              <ng-container class="max-h-full">
                @if (departmentEntries.length > 0) {
                  <ng-container
                    [ngTemplateOutlet]="navigationSection"
                    [ngTemplateOutletContext]="{
                      entries: departmentEntries,
                    }" />
                }
              </ng-container>
            }
          } @else {
            <pxc-sidebar-department-skeleton [collapsed]="collapsed" />
          }
          <ng-container
            [ngTemplateOutlet]="navigationSection"
            [ngTemplateOutletContext]="{
              entries: menuBuilderService.userEntries,
            }" />
        </div>
      </tui-scrollbar>
    }

    <div class="flex flex-col gap-2 pb-3 pt-2 transition-all" [ngClass]="collapsed ? 'px-1' : 'px-3'">
      <pxc-user-account-card [collapsed]="collapsed" />

      @if (sidebarService.shouldDisplayDataConsumption$ | async; as shouldDisplayDataConsumption) {
        <pxc-data-consumption class="mx-2" data-testid="data-consumption" [collapsed]="collapsed" />
      }

      @if (sidebarService.isActiveLicenseStateLimited$ | async; as isActiveLicenseLimited) {
        <div
          appearance="warning"
          data-testid="alert"
          size="m"
          tuiNotification
          [attr.data-collapsed]="collapsed"
          [tuiHint]="collapsed ? licenseState : null">
          <span class="line-clamp-1 text-xs" [animateFade]="!collapsed">
            <ng-container [ngTemplateOutlet]="licenseState" />
          </span>
        </div>
      }
    </div>
  </div>

  <ng-template #navigationSection let-entries="entries" let-footer="footer" let-header="header">
    <div class="flex flex-col gap-2 overflow-x-hidden py-3 transition-all" [ngClass]="collapsed ? 'px-1' : 'px-3'">
      @if (header) {
        <ng-container [ngTemplateOutlet]="header" />
      }

      <ng-container *ngTemplateOutlet="itemsSection; context: { items: entries }" />

      @if (footer) {
        <ng-container [ngTemplateOutlet]="footer" />
      }
    </div>
  </ng-template>

  <ng-template #itemsSection let-iconsColor="iconsColor" let-items="items">
    <div class="flex w-full flex-col">
      @for (entry of items; track entry.title + entry.id) {
        <ng-container
          *ngTemplateOutlet="
            !!entry.children ? folderItem : navItem;
            context: { item: entry, iconColor: iconsColor }
          " />
      }
    </div>
  </ng-template>

  <ng-template #navItem let-entry="item" let-iconColor="iconColor">
    <a
      appearance="navigation"
      class="select-none justify-center data-[collapsed=true]:gap-0!"
      pxc-sidebar-link
      queryParamsHandling="merge"
      routerLinkActive="tui-link-active tui-link-current"
      tuiHintDirection="end"
      tuiLink
      [attr.data-testid]="entry.testId"
      [collapsed]="collapsed"
      [iconStart]="entry.icon"
      [ngStyle]="{
        color: collapsed ? iconColor : null,
        position: 'relative',
      }"
      [queryParams]="entry.queryParams"
      [routerLink]="entry.link"
      [tuiHint]="collapsed ? entry.title : null">
      @if (!collapsed) {
        <div class="flex w-full items-center justify-between">
          <span class="line-clamp-1 break-all">
            {{ entry.title }}
          </span>

          @let counter = entry.count | async;
          @if (counter) {
            <span
              appearance="custom"
              class="right-3 top-0 bg-foreground-error-primary!"
              size="s"
              tuiBadge
              [ngClass]="{
                'absolute!': collapsed,
                'static!': !collapsed,
              }">
              {{ counter }}
            </span>
          }
        </div>
      }
    </a>
  </ng-template>

  <ng-template #folderItem let-item="item">
    @let isExpanded = (openDepartment$ | async) === item.id;
    <a
      appearance="navigation"
      pxc-sidebar-link
      tuiHintDirection="end"
      tuiLink
      [collapsed]="collapsed"
      [iconEnd]="collapsed ? '' : '@tui.chevron-down'"
      [ngClass]="{
        'justify-center': collapsed,
        'tui-link-active': isExpanded,
      }"
      [tuiChevron]="isExpanded"
      [tuiHint]="collapsed ? item.title : null"
      (click)="toggleFolder(item.id, isExpanded)">
      <div class="flex select-none items-center" [ngClass]="collapsed ? 'w-6 overflow-hidden' : 'w-full'">
        <pxc-department-avatar [name]="item.title" />
        <span
          animateDirection="left"
          class="line-clamp-1 flex-1 break-all pl-3"
          truncatedText
          tuiHintDirection="end"
          [animateFade]="!collapsed"
          [tuiHint]="isTitleTruncated ? item.title : null"
          (truncated)="isTitleTruncated = $event">
          {{ item.title }}
        </span>
      </div>
    </a>
    <tui-expand [expanded]="isExpanded">
      <div class="my-2">
        <ng-container
          *ngTemplateOutlet="itemsSection; context: { items: item.children, iconsColor: item.style.textColor }" />
      </div>
    </tui-expand>
  </ng-template>

  <ng-template #licenseState>
    @switch (sidebarService.activeLicenseState$ | async) {
      @case (LicenseState.DISABLED) {
        {{ t('Votre licence est désactivée.') }}
      }
      @case (LicenseState.LIMITED_QRCODE) {
        {{ t('Votre licence est limitée.') }}
      }
    }
  </ng-template>
</ng-container>
